import { makeAutoObservable, runInAction } from 'mobx';

import {
  referralStatsService,
  type ReferralStatsResponse,
} from '@/services/referralStats';

/**
 * Refer screen's three top-line numbers (Clicks / Conversions / Earnings)
 * + per-link breakdown for the channel filter chips. Backed by
 * `GET /api/v1/referrals/stats`.
 *
 * The store keeps a single in-flight request per filter scope. Switching
 * the chip cancels nothing — it just kicks off a new fetch and the
 * latest one wins. With one user and one network, that's sufficient.
 */
class ReferralStatsStore {
  data: ReferralStatsResponse | null = null;
  loading = false;
  error: string | null = null;
  /** `null` means "all of the user's links". */
  scope: number | null = null;

  /**
   * Per-deal totals for a deal's referral screen, cached beside the global
   * numbers so opening Invest never disturbs what the index shows. The
   * server scopes paid / in-progress / earnings / left-on-the-table to the
   * deal (and quotes THAT deal's cut); the client only asks.
   */
  byDeal = new Map<number, ReferralStatsResponse>();
  /** Last failed per-deal fetch, so a screen with nothing cached can say so. */
  dealError = new Map<number, string>();
  private dealLoading = new Set<number>();
  /**
   * Per-deal request generation. A pull-to-refresh, an undo and a settle
   * timer can all fire `refreshDeal` for the same deal within seconds; only
   * the newest response may land, or an older one can put a paid friend
   * back into "this close".
   */
  private dealGeneration = new Map<number, number>();

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setScope(referralLinkId: number | null): void {
    if (this.scope === referralLinkId) return;
    this.scope = referralLinkId;
    // Drop the previous scope's totals before the new fetch lands so the
    // 3-card grid renders zeros (or a skeleton via `?? 0`) during the
    // transition rather than flashing stale Clicks/Conversions/Earnings.
    this.data = null;
    void this.refresh();
  }

  /**
   * Refetch the global numbers AND every per-deal entry already cached: a
   * pull on the index must not leave a deal screen reopening onto counts
   * from before a friend paid.
   */
  async refresh(): Promise<void> {
    this.loading = true;
    this.error = null;
    const deals = [...this.byDeal.keys()].map((dealId) => this.refreshDeal(dealId));
    try {
      const data = await referralStatsService.fetch(this.scope ?? undefined);
      runInAction(() => {
        this.data = data;
        this.loading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Could not load stats.';
        this.loading = false;
      });
    }
    await Promise.all(deals);
  }

  /** Convenience for the Refer screen. Idempotent on repeated mounts. */
  async ensureLoaded(): Promise<void> {
    if (this.data || this.loading) return;
    await this.refresh();
  }

  forDeal(dealId: number): ReferralStatsResponse | null {
    return this.byDeal.get(dealId) ?? null;
  }

  forDealError(dealId: number): string | null {
    return this.dealError.get(dealId) ?? null;
  }

  async refreshDeal(dealId: number): Promise<void> {
    const generation = (this.dealGeneration.get(dealId) ?? 0) + 1;
    this.dealGeneration.set(dealId, generation);
    this.dealLoading.add(dealId);
    try {
      const data = await referralStatsService.fetch({ dealId });
      if (this.dealGeneration.get(dealId) !== generation) return; // superseded
      runInAction(() => {
        this.byDeal.set(dealId, data);
        this.dealError.delete(dealId);
      });
    } catch (err) {
      if (this.dealGeneration.get(dealId) !== generation) return;
      runInAction(() => {
        // Whatever was cached stays; the screen decides whether "stale" or
        // "nothing yet" is the state to show.
        this.dealError.set(dealId, err instanceof Error ? err.message : 'Could not load stats.');
      });
    } finally {
      if (this.dealGeneration.get(dealId) === generation) {
        runInAction(() => {
          this.dealLoading.delete(dealId);
        });
      }
    }
  }

  async ensureDealLoaded(dealId: number): Promise<void> {
    if (this.byDeal.has(dealId) || this.dealLoading.has(dealId)) return;
    await this.refreshDeal(dealId);
  }

  reset(): void {
    this.data = null;
    this.loading = false;
    this.error = null;
    this.scope = null;
    this.byDeal.clear();
    this.dealError.clear();
    this.dealLoading.clear();
    this.dealGeneration.clear();
  }
}

export const referralStatsStore = new ReferralStatsStore();
